import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
const DEFAULT_ZOOM_INDEX = 2; // 100%
/**
 * Full-featured PDF viewer component.
 *
 * Features:
 * - Page rendering with zoom controls
 * - Print document
 * - Download document
 * - Page counter and navigation
 * - Fit to width
 * - Scroll-to-end detection (for mandatory reading acknowledgment)
 */
export function PdfViewer({ url, fileName, onReachedEnd, showToolbar = true }) {
    const [numPages, setNumPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef(null);
    const hasReachedEnd = useRef(false);
    const pageRefs = useRef(new Map());
    const zoom = ZOOM_LEVELS[zoomIndex];
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(false);
    };
    const onDocumentLoadError = () => {
        setLoading(false);
        setError(true);
    };
    const handleScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container)
            return;
        // Track current page based on scroll position
        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrollPercent = scrollTop / (scrollHeight - clientHeight);
        const estimatedPage = Math.min(numPages, Math.max(1, Math.ceil(scrollPercent * numPages)));
        setCurrentPage(estimatedPage);
        // Detect end of document
        if (!hasReachedEnd.current && onReachedEnd) {
            if (scrollTop + clientHeight >= scrollHeight - 50) {
                hasReachedEnd.current = true;
                onReachedEnd();
            }
        }
    }, [numPages, onReachedEnd]);
    const handleZoomIn = () => setZoomIndex(i => Math.min(ZOOM_LEVELS.length - 1, i + 1));
    const handleZoomOut = () => setZoomIndex(i => Math.max(0, i - 1));
    const handleZoomReset = () => setZoomIndex(DEFAULT_ZOOM_INDEX);
    const handleFitWidth = () => {
        const containerWidth = containerRef.current?.clientWidth || 700;
        const baseWidth = 700;
        const targetZoom = (containerWidth - 40) / baseWidth;
        const closest = ZOOM_LEVELS.reduce((prev, curr, idx) => Math.abs(curr - targetZoom) < Math.abs(ZOOM_LEVELS[prev] - targetZoom) ? idx : prev, 0);
        setZoomIndex(closest);
    };
    const handlePrint = () => {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
            printWindow.addEventListener('load', () => {
                printWindow.print();
            });
        }
    };
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'documento.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleGoToPage = (pageNum) => {
        const page = pageRefs.current.get(pageNum);
        if (page) {
            page.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };
    const baseWidth = containerRef.current?.clientWidth
        ? Math.min(containerRef.current.clientWidth - 40, 700)
        : 700;
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }, children: [showToolbar && (_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 0.75rem',
                    background: '#f7fafc',
                    borderBottom: '1px solid #e2e8f0',
                    borderRadius: '0.25rem 0.25rem 0 0',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.25rem' }, children: [_jsx(ToolbarButton, { onClick: handleZoomOut, disabled: zoomIndex === 0, title: "Zoom out", children: "\u2212" }), _jsxs(ToolbarButton, { onClick: handleZoomReset, title: "Ripristina zoom", style: { minWidth: '50px' }, children: [Math.round(zoom * 100), "%"] }), _jsx(ToolbarButton, { onClick: handleZoomIn, disabled: zoomIndex === ZOOM_LEVELS.length - 1, title: "Zoom in", children: "+" }), _jsx(ToolbarButton, { onClick: handleFitWidth, title: "Adatta alla larghezza", children: "\u2194" })] }), _jsx("div", { style: { width: '1px', height: '20px', background: '#cbd5e0', margin: '0 0.5rem' } }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem' }, children: [_jsx(ToolbarButton, { onClick: () => handleGoToPage(Math.max(1, currentPage - 1)), disabled: currentPage <= 1, title: "Pagina precedente", children: "\u2039" }), _jsx("span", { style: { minWidth: '80px', textAlign: 'center', color: '#4a5568' }, children: numPages > 0 ? `${currentPage} / ${numPages}` : '...' }), _jsx(ToolbarButton, { onClick: () => handleGoToPage(Math.min(numPages, currentPage + 1)), disabled: currentPage >= numPages, title: "Pagina successiva", children: "\u203A" })] }), _jsx("div", { style: { width: '1px', height: '20px', background: '#cbd5e0', margin: '0 0.5rem' } }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.25rem' }, children: [_jsx(ToolbarButton, { onClick: handlePrint, title: "Stampa", children: "\uD83D\uDDA8\uFE0F" }), _jsx(ToolbarButton, { onClick: handleDownload, title: "Scarica", children: "\uD83D\uDCBE" })] })] })), _jsxs("div", { ref: containerRef, onScroll: handleScroll, style: {
                    flex: 1,
                    overflow: 'auto',
                    background: '#525659',
                    borderRadius: showToolbar ? '0 0 0.25rem 0.25rem' : '0.25rem',
                    padding: '1rem 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.75rem',
                }, children: [loading && (_jsx("div", { style: { color: '#fff', padding: '2rem' }, children: "Caricamento documento..." })), error && (_jsxs("div", { style: { color: '#fff', padding: '2rem', textAlign: 'center' }, children: [_jsx("div", { style: { fontSize: '2rem', marginBottom: '0.5rem' }, children: "\u26A0\uFE0F" }), _jsx("div", { children: "Documento non disponibile." }), _jsx("div", { style: { fontSize: '0.85rem', color: '#a0aec0', marginTop: '0.5rem' }, children: "Il file non \u00E8 presente nel sistema. Contatta l'Ufficio Personale se il problema persiste." })] })), !error && (_jsx(Document, { file: url, onLoadSuccess: onDocumentLoadSuccess, onLoadError: onDocumentLoadError, loading: "", children: Array.from({ length: numPages }, (_, i) => (_jsx("div", { ref: (el) => { if (el)
                                pageRefs.current.set(i + 1, el); }, children: _jsx(Page, { pageNumber: i + 1, width: baseWidth * zoom, renderTextLayer: false, renderAnnotationLayer: false }) }, i + 1))) }))] })] }));
}
// ─── Toolbar Button ─────────────────────────────────────────────────────────
function ToolbarButton({ onClick, disabled, title, children, style }) {
    return (_jsx("button", { onClick: onClick, disabled: disabled, title: title, style: {
            padding: '0.3rem 0.5rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: '1px solid #cbd5e0',
            borderRadius: '4px',
            background: disabled ? '#edf2f7' : '#fff',
            color: disabled ? '#a0aec0' : '#2d3748',
            fontSize: '0.85rem',
            lineHeight: 1,
            opacity: disabled ? 0.6 : 1,
            ...style,
        }, children: children }));
}
//# sourceMappingURL=PdfViewer.js.map